if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // START CUSTOM FILTER LOGIC
        // This fires the filter logic on initial page load.
        if (this.dataset.enableAltFiltering === 'true') {
          // Pass true for 'isPageLoad' flag to fix mobile race condition
          window.addEventListener('load', () => this.filterMediaByOption1(null, true), { once: true });
        }
        // END CUSTOM FILTER LOGIC
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);
          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
          // START CUSTOM FILTER LOGIC
          this.filterMediaByOption1(variant, false);
          // END CUSTOM FILTER LOGIC
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;
        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }
      
      // START CUSTOM FILTER LOGIC
      /**
       * Filters the media gallery based on ALL variant options.
       * Uses "Inclusive Matching": Shows family + specific, hides explicit mismatches.
       * FIX: Finds inputs via fieldset index to avoid name mismatch issues.
       */
      filterMediaByOption1(variant = null, isPageLoad = false) {
        try {
          // --- 1. TAG & SAFETY CHECKS ---
          if (this.dataset.enableAltFiltering !== 'true') {
            if (isPageLoad) this.showAllMedia(isPageLoad);
            return;
          }

          const mediaGallery = this.querySelector(`media-gallery`);
          if (!mediaGallery) return;

          // --- 2. GET SELECTED OPTION VALUES ---
          let selectedOptions = [null, null, null]; // [Option1, Option2, Option3]

          if (variant) {
            selectedOptions[0] = variant.option1;
            selectedOptions[1] = variant.option2;
            selectedOptions[2] = variant.option3;
          } else {
            // Fallback using Variant JSON (Most reliable on page load)
            const variantSelects = this.querySelector('variant-selects');
            if (variantSelects) {
              const selectedVariantData = variantSelects.querySelector('[data-selected-variant]')?.innerHTML;
              if (selectedVariantData) {
                const v = JSON.parse(selectedVariantData);
                selectedOptions[0] = v.option1;
                selectedOptions[1] = v.option2;
                selectedOptions[2] = v.option3;
              }
            }
            
            // Deep Fallback: Read inputs directly by FIELDSET INDEX (Name Agnostic)
            if (!selectedOptions[0]) {
               const fieldsets = this.querySelectorAll('variant-selects fieldset');
               fieldsets.forEach((fieldset, index) => {
                   if(index > 2) return; // Only care about first 3
                   const checked = fieldset.querySelector('input:checked, select option:checked');
                   if(checked) selectedOptions[index] = checked.value;
               });
            }
          }

          if (!selectedOptions[0]) return; // Option 1 is mandatory

          // --- 3. NORMALIZE HELPERS ---
          const cleanText = (str) => str ? str.toLowerCase().replace(/\//g, '-') : '';
          const cleanFile = (str) => cleanText(str).replace(/\s+/g, '');

          const selectedOp1 = cleanText(selectedOptions[0]);
          const selectedOp1File = cleanFile(selectedOptions[0]);

          // --- 4. BUILD EXCLUSION LIST (The "Cousins") ---
          // FIX: We now use FIELDSETS to find the inputs, ignoring their 'name' attribute.
          let exclusionTerms = []; 
          let exclusionFiles = [];
          
          const fieldsets = this.querySelectorAll('variant-selects fieldset');

          [1, 2].forEach(optionIndex => { 
             // optionIndex 1 = Option 2 (Fieldset index 1)
             // optionIndex 2 = Option 3 (Fieldset index 2)
             const currentVal = selectedOptions[optionIndex];
             
             // Only proceed if we have a selected value AND the fieldset exists
             if (currentVal && fieldsets[optionIndex]) {
                 const inputs = fieldsets[optionIndex].querySelectorAll('input, select option');
                 inputs.forEach(input => {
                     // If this option exists but is NOT selected, it's a "cousin" to exclude
                     if (input.value && input.value !== currentVal) {
                         exclusionTerms.push(cleanText(input.value));
                         exclusionFiles.push(cleanFile(input.value));
                     }
                 });
             }
          });

          // --- 5. FILTER LOOP ---
          const mainMediaList = mediaGallery.querySelector('.product__media-list');
          const thumbnailList = mediaGallery.querySelector('.thumbnail-list');

          const filterLists = (list, itemSelector) => {
             if (!list) return;
             const items = list.querySelectorAll(itemSelector);

             items.forEach(item => {
                const altText = cleanText(item.dataset.altText || '');
                const fileName = cleanText(item.dataset.filename || '');

                // CHECK 1: FAMILY MATCH (Must match Option 1)
                // Uses .includes() to handle prefixes like 'shaft-'
                const matchesFamily = altText.includes(selectedOp1) || fileName.includes(selectedOp1File);

                if (!matchesFamily) {
                    item.hidden = true;
                    item.style.display = 'none';
                    return;
                }

                // CHECK 2: COUSIN EXCLUSION (Must NOT match other joints)
                let isExcluded = false;
                
                if (exclusionTerms.some(term => altText.includes(term))) {
                    isExcluded = true;
                }
                else if (exclusionFiles.some(fileTerm => fileName.includes(fileTerm))) {
                    isExcluded = true;
                }

                if (isExcluded) {
                    item.hidden = true;
                    item.style.display = 'none';
                } else {
                    item.hidden = false;
                    item.style.display = '';
                }
             });
          };

          filterLists(mainMediaList, 'li.product__media-item');
          filterLists(thumbnailList, 'li.thumbnail-list__item');

          // --- 6. FALLBACK: NO RESULTS ---
          let visibleCount = 0;
          if (mainMediaList) {
             visibleCount = mainMediaList.querySelectorAll('li.product__media-item:not([hidden]):not([style*="display: none"])').length;
          }
          
          if (visibleCount === 0) {
             this.showAllMedia(false); 
             if (mainMediaList) visibleCount = mainMediaList.querySelectorAll('li.product__media-item').length;
          }

          // --- 7. UI UPDATES ---
          const counterCurrent = mediaGallery.querySelector('.slider-counter--current');
          const counterTotal = mediaGallery.querySelector('.slider-counter--total');
          if (counterCurrent) counterCurrent.textContent = '1';
          if (counterTotal) counterTotal.textContent = visibleCount > 0 ? visibleCount : '0';

          // Reset Gallery Position
          if (isPageLoad) {
            setTimeout(() => {
              this.resetGalleryToFirstVisible(mediaGallery, mainMediaList, thumbnailList);
            }, 100);
          } else {
            this.resetGalleryToFirstVisible(mediaGallery, mainMediaList, thumbnailList);
          }

        } catch (e) {
          console.error('Error during inclusive media filtering:', e);
        }
      }

      showAllMedia(isPageLoad = false) {
        try {
          const mediaGallery = this.querySelector(`media-gallery`);
          if (!mediaGallery) return;

          const mainMediaList = mediaGallery.querySelector('.product__media-list');
          const thumbnailList = mediaGallery.querySelector('.thumbnail-list');

          let totalCount = 0;
          if (mainMediaList) {
            const mediaItems = mainMediaList.querySelectorAll('li.product__media-item');
            mediaItems.forEach(item => {
              item.hidden = false;
              item.style.display = '';
            });
            totalCount = mediaItems.length;
          }

          if (thumbnailList) {
            const thumbItems = thumbnailList.querySelectorAll('li.thumbnail-list__item');
            thumbItems.forEach(item => {
              item.hidden = false;
              item.style.display = '';
            });
          }

          const counterCurrentElement = mediaGallery.querySelector('.slider-counter--current');
          const counterTotalElement = mediaGallery.querySelector('.slider-counter--total');
          
          if (counterCurrentElement) {
            counterCurrentElement.textContent = '1';
          }
          if (counterTotalElement) {
            counterTotalElement.textContent = totalCount;
          }

          if (isPageLoad) {
            setTimeout(() => {
              this.resetGalleryToFirstVisible(mediaGallery, mainMediaList, thumbnailList);
            }, 100);
          } else {
            this.resetGalleryToFirstVisible(mediaGallery, mainMediaList, thumbnailList);
          }
        } catch (e) {
          console.error('Error in showAllMedia:', e);
        }
      }

      resetGalleryToFirstVisible(mediaGallery, mainMediaList, thumbnailList) {
        let firstVisibleMediaId = null;
        if (mainMediaList) {
          mainMediaList.querySelectorAll('li.product__media-item.is-active').forEach(item => {
            item.classList.remove('is-active');
          });
          const firstVisibleSlide = mainMediaList.querySelector('li.product__media-item:not([hidden]):not([style*="display: none"])');
          if (firstVisibleSlide) {
            firstVisibleSlide.classList.add('is-active');
            firstVisibleMediaId = firstVisibleSlide.dataset.mediaId;
          }
        }

        if (thumbnailList) {
          thumbnailList.querySelectorAll('button[aria-current="true"]').forEach(btn => btn.removeAttribute('aria-current'));
          const firstVisibleThumb = thumbnailList.querySelector('li.thumbnail-list__item:not([hidden]):not([style*="display: none"])');
          if (firstVisibleThumb) {
            const thumbButton = firstVisibleThumb.querySelector('button');
            if (thumbButton) {
              thumbButton.setAttribute('aria-current', 'true');
              if (!firstVisibleMediaId) { 
                 firstVisibleMediaId = firstVisibleThumb.dataset.target;
              }
            }
          }
        }

        if (firstVisibleMediaId) {
          mediaGallery.setActiveMedia?.(firstVisibleMediaId, true);
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection ||
          this.dataset.section;
      }
    }
  );
}